import { useNavigate } from 'react-router-dom';
import { Flame, Zap, ChevronRight, Trophy } from 'lucide-react';
import { XPProgressBar } from './XPProgressBar';
import { ACHIEVEMENTS } from '@/store/useGamificationStore';

interface PostQuizChallengeProps {
  examId: string;
  categoryId?: string;
  score: number;
  streak: number;
  xpGained: number;
  xpProgress: { current: number; needed: number; percent: number };
  level: number;
  newAchievements: string[];
  onRestart: () => void;
}

export function PostQuizChallenge({
  examId, categoryId, score, streak, xpGained, xpProgress, level, newAchievements, onRestart
}: PostQuizChallengeProps) {
  const navigate = useNavigate();

  return (
    <div className="space-y-4 mt-6 animate-in fade-in slide-in-from-bottom-4 duration-500">
      {/* XP Gain */}
      <div className="bg-violet-500/5 border border-violet-500/15 rounded-2xl p-4 text-center">
        <div className="flex items-center justify-center gap-2 mb-3">
          <Zap className="w-5 h-5 text-violet-400" />
          <span className="text-lg font-bold text-violet-400">+{xpGained} XP</span>
        </div>
        <XPProgressBar currentXP={xpProgress.current} needed={xpProgress.needed} percent={xpProgress.percent} level={level} />
      </div>

      {/* Streak */}
      {streak >= 2 && (
        <div className="bg-orange-500/5 border border-orange-500/15 rounded-xl p-3 flex items-center gap-3">
          <Flame className="w-5 h-5 text-orange-400" />
          <div>
            <p className="text-xs font-semibold text-orange-400">{streak} quiz d'affilée !</p>
            <p className="text-[10px] text-fg-faint">Continuez pour débloquer des récompenses</p>
          </div>
        </div>
      )}

      {/* New Achievements */}
      {newAchievements.length > 0 && (
        <div className="space-y-2">
          {newAchievements.map(id => {
            const achievement = ACHIEVEMENTS.find(a => a.id === id);
            if (!achievement) return null;
            return (
              <div key={id} className="bg-yellow-500/5 border border-yellow-500/15 rounded-xl p-3 flex items-center gap-3">
                <span className="text-xl">{achievement.icon}</span>
                <div>
                  <p className="text-xs font-semibold text-yellow-400">{achievement.name}</p>
                  <p className="text-[10px] text-fg-faint">{achievement.description}</p>
                </div>
                <Trophy className="w-4 h-4 text-yellow-400/40 ml-auto" />
              </div>
            );
          })}
        </div>
      )}

      {/* Challenge Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        <button
          onClick={onRestart}
          className="group bg-green-500/5 border border-green-500/15 rounded-xl p-4 text-left hover:bg-green-500/10 transition-colors"
        >
          <p className="text-xs font-semibold text-green-400 mb-1">Nouveau défi</p>
          <p className="text-[10px] text-fg-faint">Continuer dans cette catégorie</p>
          <ChevronRight className="w-4 h-4 text-green-400/40 mt-2 group-hover:translate-x-1 transition-transform" />
        </button>

        <button
          onClick={() => navigate(`/exam/${examId}`)}
          className="group bg-blue-500/5 border border-blue-500/15 rounded-xl p-4 text-left hover:bg-blue-500/10 transition-colors"
        >
          <p className="text-xs font-semibold text-blue-400 mb-1">Explorer</p>
          <p className="text-[10px] text-fg-faint">Essayer une autre catégorie</p>
          <ChevronRight className="w-4 h-4 text-blue-400/40 mt-2 group-hover:translate-x-1 transition-transform" />
        </button>
      </div>
    </div>
  );
}
